import i18n from 'i18next';

class NotePresenter {
  constructor(note) {
    this.note = note;
    if (note?.gen_patient_updates) {
      this.patientUpdates = Object.entries(note.gen_patient_updates).reduce((acc, [key, value]) => {
        if (key.startsWith('gen_')) {
          acc[key.substring(4)] = value;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
    }
  }

  getUUID() {
    return this.note?.uuid;
  }

  getBody() {
    return this.note?.user_body ? this.note.user_body : this.note?.gen_body;
  }

  getSummary() {
    return this.note?.gen_summary || (this.note?.gen_body?.substring(0, 150).replace(/#/g, "") + '...');
  }

  hasInsights() {
    return Boolean(
      this.getConsiderations() || this.getInconsistencies()
    );
  }

  getConsiderations() {
    return this.note?.gen_considerations;
  }

  getInconsistencies() {
    return this.note?.gen_inconsistencies;
  }

  // returns true if there are any values in the patient updates
  hasPatientUpdates() {
    if (!this.patientUpdates) return false;
    
    return Object.values(this.patientUpdates).some(value => value !== null && value !== undefined && value !== '');
  }

  // returns the patient updates if there are any
  getPatientUpdates() {
    if (!this.hasPatientUpdates()) return null;
    return this.patientUpdates;
  }

  getMedications() {
    console.log('[NotePresenter] Note data:', this.note);
    console.log('[NotePresenter] gen_medications:', this.note?.gen_medications);
    
    // Handle the new array structure
    if (Array.isArray(this.note?.gen_medications)) {
      return this.note.gen_medications;
    }
    
    // Fallback for old structure or null/undefined
    return this.note?.gen_medications || [];
  }

  getMedicationStrings() {
    const medications = this.getMedications();
    return medications
      .filter(medication => 
        medication && 
        medication.name && 
        typeof medication.name === 'string' && 
        medication.name.trim().length > 0
      )
      .map(medication => {
        const name = medication.name.trim();
        return medication.dosage && medication.dosage.trim()
          ? `${name} - ${medication.dosage.trim()}`
          : name;
      })
      .filter(medString => medString && medString.trim().length > 0); // Final safety filter
  }

  getActiveMedicationStrings() {
    const medications = this.getMedications();
    return medications
      .filter(medication => 
        medication && 
        medication.name && 
        typeof medication.name === 'string' && 
        medication.name.trim().length > 0 &&
        medication.status !== 'stopped'
      )
      .map(medication => {
        const name = medication.name.trim();
        return medication.dosage && medication.dosage.trim()
          ? `${name} - ${medication.dosage.trim()}`
          : name;
      })
      .filter(medString => medString && medString.trim().length > 0); // Final safety filter
  }

  getNonPharmaceuticalTreatments() {
    // Handle the new array structure
    if (Array.isArray(this.note?.gen_non_pharmaceutical_treatments)) {
      return this.note.gen_non_pharmaceutical_treatments;
    }
    
    // Fallback for old structure or null/undefined
    return this.note?.gen_non_pharmaceutical_treatments || [];
  }

  getNonPharmaceuticalTreatmentStrings() {
    const treatments = this.getNonPharmaceuticalTreatments();
    return treatments.map(treatment => {
      return treatment.dosage 
        ? `${treatment.name} - ${treatment.dosage}`
        : treatment.name;
    });
  }

  getActiveNonPharmaceuticalTreatmentStrings() {
    const treatments = this.getNonPharmaceuticalTreatments();
    return treatments
      .filter(treatment => treatment.status !== 'stopped')
      .map(treatment => {
        return treatment.dosage 
          ? `${treatment.name} - ${treatment.dosage}`
          : treatment.name;
      });
  }

  setUserBody(body) {
    if (!this.note) {
      throw new Error('Cannot set user body on a null note.');
    }
    this.note.user_body = body;
  }

  isDoctorNote() {
    return Boolean(
      this.note &&
      this.note.note_type_slug &&
      this.note.note_type_slug !== 'patient_instructions'
    );
  }

  isComplete() {
    return Boolean(this.note && this.note.completed && this.getBody());
  }

  isValid() {
    return Boolean(
      this.note &&
      this.isComplete() &&
      this.getBody() &&
      !this.note.problem
    );
  }

  getTitle() {
    return this.note?.gen_title || i18n.t(`session_details.note_types.${this.getNoteType()}`) || this.note?.default_title;
  }

  getNoteType() {
    return this.note?.note_type_slug;
  }

  canBeRegenerated() {
    return Boolean(this.isValid());
  }

  getCreatedAt() {
    return this.note?.created_at;
  }
}

export default NotePresenter;
